import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Container, Paper, List, ListItem, ListItemText, Divider, CircularProgress } from '@mui/material';
import { collection, getDocs, doc, getDoc, writeBatch, arrayUnion, addDoc, serverTimestamp, where, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { SEASON_STATUS } from '../../services/seasons';
import PageLayout from '../../components/PageLayout';
import { page } from '../../theme/commonStyles';

function JoinAuction() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const notify = useNotification();

    useEffect(() => {
        const fetchAuctionsAndRequests = async () => {
            setLoading(true);
            try {
                // 1. Fetch all auctions
                const auctionsQuery = await getDocs(collection(db, 'auctions'));
                let auctionsList = auctionsQuery.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                auctionsList = auctionsList.filter(auction => auction.status !== 'Test');

                // Veilingen van een afgerond seizoen niet tonen: daar kun je niet meer
                // aan deelnemen.
                const seasonsSnap = await getDocs(collection(db, 'seasons'));
                const afgerondeSeizoenen = new Set(
                    seasonsSnap.docs
                        .filter(d => d.data().status === SEASON_STATUS.AFGEROND)
                        .map(d => d.id)
                );
                auctionsList = auctionsList.filter(auction => !afgerondeSeizoenen.has(String(auction.season)));

                if (user) {
                    // For each auction, check if the user has a pending join request.
                    const auctionsWithRequestStatus = await Promise.all(auctionsList.map(async (auction) => {
                        const requestsQuery = query(
                            collection(db, 'auctions', auction.id, 'joinRequests'),
                            where('uid', '==', user.uid)
                        );
                        const requestSnapshot = await getDocs(requestsQuery);
                        return {
                            ...auction,
                            hasRequested: !requestSnapshot.empty
                        };
                    }));
                    setAuctions(auctionsWithRequestStatus);
                } else {
                    setAuctions(auctionsList);
                }

            } catch (err) {
                notify.error('Fout bij het ophalen van veilingen.');
                console.error(err);
            }
            setLoading(false);
        };

        fetchAuctionsAndRequests();
    }, [user, notify]);

    const handleJoin = async (auction) => {
        if (!user) {
            navigate('/login');
            return;
        }

        try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            const userName = userDoc.data()?.name || user.email;

            if (auction.isOpen) {
                const batch = writeBatch(db);
                const auctionRef = doc(db, 'auctions', auction.id);
                const participantRef = doc(db, 'auctions', auction.id, 'participants', user.uid);

                // 1. Add user to the participants subcollection
                batch.set(participantRef, {
                    uid: user.uid,
                    email: user.email,
                    name: userName,
                    budget: auction.startBudget || 120
                });

                // 2. Add user's UID to the participantUids array for easy querying and rules
                batch.update(auctionRef, { participantUids: arrayUnion(user.uid) });

                await batch.commit();
                alert(`Je neemt nu deel aan de veiling: ${auction.name}`);
            } else {
                const joinRequestsRef = collection(db, 'auctions', auction.id, 'joinRequests');
                await addDoc(joinRequestsRef, {
                    uid: user.uid,
                    email: user.email,
                    name: userName,
                    timestamp: serverTimestamp()
                });
                alert(`Een verzoek om deel te nemen aan '${auction.name}' is naar de beheerder gestuurd.`);
                // Optimistically update the UI to disable the button
                setAuctions(auctions => auctions.map(a => a.id === auction.id ? { ...a, hasRequested: true } : a));
            }
        } catch (err) {
            notify.error('Er is een fout opgetreden.');
            console.error(err);
        }
    };

    if (loading) {
        return <Container sx={page.loadingContainer}><CircularProgress /></Container>;
    }

    return (
        <PageLayout>
            <Container component="main" maxWidth="md">
                <Paper
                    elevation={6}
                    sx={{
                        marginTop: 8,
                        padding: 4,
                    }}
                >
                    <Typography component="h1" variant="h4" gutterBottom align="center">
                        Neem deel aan een veiling
                    </Typography>
                    <List>
                        {auctions.length === 0 && <Typography align="center">Er zijn momenteel geen veilingen beschikbaar.</Typography>}
                        {auctions.map((auction, index) => (
                            <React.Fragment key={auction.id}>
                                <ListItem
                                    secondaryAction={
                                        <Button
                                            variant="contained"
                                            onClick={() => handleJoin(auction)}
                                            disabled={(auction.participantUids && auction.participantUids.includes(user?.uid)) || auction.hasRequested}
                                        >
                                            {auction.isOpen ? 'Deelnemen' : 'Aanvragen'}
                                        </Button>
                                    }
                                >
                                    <ListItemText
                                        primary={auction.name}
                                        secondary={`Status: ${auction.isOpen ? 'Open' : 'Besloten'} | Deelnemers: ${auction.participantUids ? auction.participantUids.length : 0}`}
                                    />
                                </ListItem>
                                {index < auctions.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            </Container>
        </PageLayout>
    );
}

export default JoinAuction;